import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Box, Typography, Button, Grid, Stack, Paper, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CountUp from 'react-countup';
import { useTranslation } from 'react-i18next';

const HeroSection = () => {
    const [memberCount, setMemberCount] = useState(null);
    const { t } = useTranslation();
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    const theme = useTheme();
    const isDownMd = useMediaQuery(theme.breakpoints.down('md'));
    const isDownSm = useMediaQuery(theme.breakpoints.down('sm'));

    const fallbackMemberCount = 1200;
    const parsedMemberCount = Number(memberCount);
    const hasValidMemberCount = Number.isFinite(parsedMemberCount) && parsedMemberCount > 0;
    const roundedMemberCount = hasValidMemberCount
      ? Math.ceil(parsedMemberCount / 100) * 100
      : fallbackMemberCount;

    const metrics = [
        {
            key: 'experience',
            value: 17,
            suffix: '+',
            label: t('hero.metrics.experience'),
        },
        {
            key: 'sessions',
            value: 6,
            suffix: '',
            label: t('hero.metrics.sessions'),
        },
        {
            key: 'members',
            value: roundedMemberCount,
            suffix: '+',
            label: t('hero.metrics.members'),
        },
    ];
    const trackClick = async (link) => {
        try {
          await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/stats/track-click`, {
            link,
          });
        } catch (error) {
          console.error('Error tracking click:', error.message);
        }
      };
    useEffect(() => {
        const fetchMemberCount = async () => {
          try {
            const response = await axios.get(`${BACKEND_URL}/api/stats/discord/memberCount`);
            const fetchedCount = Number(response.data?.memberCount);
            setMemberCount(Number.isFinite(fetchedCount) ? fetchedCount : null);
          } catch (error) {
            console.error('Error fetching Discord member count:', error);
          }
        };
    
        fetchMemberCount();
      }, [BACKEND_URL]);

    return (
        <Box
            sx={{
                position: 'relative',
                overflow: 'hidden',
                color: '#fff',
                background: 'radial-gradient(circle at top, rgba(14,24,54,0.95), rgba(5,11,30,0.98))',
                minHeight: isDownSm ? '75vh' : '85vh',
                display: 'flex',
                alignItems: 'center',
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    inset: 0,
                    backgroundImage: `url('FlockTradeHeroSection.webp')`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    opacity: 0.25,
                }}
            />
            <Box
                sx={{
                    position: 'relative',
                    zIndex: 1,
                    width: '100%',
                    px: { xs: 2.5, sm: 4, md: 8 },
                    py: { xs: 10, md: 14 },
                    maxWidth: '1200px',
                    mx: 'auto',
                }}
            >
                <Stack spacing={{ xs: 6, md: 8 }}>
                    <Stack spacing={3} maxWidth={isDownMd ? '100%' : '65%'}>
                        <Typography
                            component="h1"
                            sx={{
                                fontWeight: 800,
                                fontSize: { xs: '2.4rem', sm: '2.8rem', md: '3.4rem' },
                                lineHeight: 1.1,
                            }}
                        >
                            {t('hero.title')}
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                color: 'rgba(255,255,255,0.75)',
                                fontSize: { xs: '1rem', md: '1.15rem' },
                                maxWidth: 540,
                            }}
                        >
                            {t('hero.subtitle')}
                        </Typography>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <Button
                                variant="contained"
                                color="secondary"
                                size={isDownSm ? 'medium' : 'large'}
                                onClick={() => {
                                    trackClick('CTA-Hero: View Memberships');
                                    window.location.href = '/membership';
                                }}
                                sx={{ fontWeight: 700 }}
                            >
                                {t('hero.primaryCta')}
                            </Button>
                            <Button
                                variant="outlined"
                                color="secondary"
                                size={isDownSm ? 'medium' : 'large'}
                                onClick={() => {
                                    trackClick('CTA-Hero: Join Discord');
                                    window.open('https://discord.com/invite/sQjPufbufK', '_blank');
                                }}
                                sx={{
                                    fontWeight: 700,
                                    borderColor: 'rgba(255,255,255,0.5)',
                                    color: '#fff',
                                    '&:hover': {
                                        borderColor: '#fff',
                                        backgroundColor: 'rgba(255,255,255,0.08)',
                                    },
                                }}
                            >
                                {t('hero.secondaryCta')}
                            </Button>
                        </Stack>
                    </Stack>

                    <Grid container spacing={{ xs: 2.5, sm: 3 }} columns={{ xs: 12, sm: 12, md: 12 }}>
                        {metrics.map((metric) => (
                            <Grid item xs={12} sm={4} key={metric.key}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        height: '100%',
                                        borderRadius: 3,
                                        backgroundColor: 'rgba(4,10,26,0.75)',
                                        border: '1px solid rgba(255,255,255,0.15)',
                                        backdropFilter: 'blur(6px)',
                                        p: { xs: 2.5, sm: 3 },
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 1,
                                    }}
                                >
                                    <Typography
                                        variant="h3"
                                        sx={{
                                            fontWeight: 800,
                                            fontSize: { xs: '2rem', md: '2.4rem' },
                                        }}
                                    >
                                        <CountUp end={metric.value} duration={1.8} />{metric.suffix}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                                        {metric.label}
                                    </Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Stack>
            </Box>
        </Box>
    );
};

export default HeroSection;
